import { memo, useCallback, useState } from 'react';
import { cn, Price } from '@divlab/divanui';

import * as ApiPecom from '@Api/Pecom';
import * as Meta from '@Queries/Meta';
import useTranslation from '@Queries/useTranslation';
import { useRequest } from '@Contexts/Request';
import InputHelperAddress from '@Components/InputHelperAddress';
import Button from '@UI/Button';
import Form from '@UI/Form';
import styles from './ShippingCostCalculator.module.css';

import type { ServerErrorsOpts } from '@UI/Form';
import type { AddressHint } from '@Components/InputHelperAddress';
import type { FC, HTMLAttributes } from 'react';

export interface ShippingCostCalculatorProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  modalView?: boolean;
  title?: string;
  onSetDeliveryPrice?: (price: number) => void;
}

const ShippingCostCalculator: FC<ShippingCostCalculatorProps> = (props) => {
  const { className, title: calculatorTitle, modalView, onSetDeliveryPrice, ...restProps } = props;
  const meta = Meta.useMeta();
  const { country, language } = useRequest();
  const { t } = useTranslation();
  const isUZB = country === 'UZB';
  const isRus = country === 'RUS';
  const [loading, setLoading] = useState<boolean>(false);
  const [price, setPrice] = useState<number>(null);
  const [serverErrors, setServerErrors] = useState<ServerErrorsOpts[]>([]);
  const [selectedHint, setSelectedHint] = useState<AddressHint>(null);
  const [hints, setHints] = useState<AddressHint[]>([]);
  const notes = isUZB
    ? [t('ui.shipping-calc.hints.1')]
    : [t('ui.shipping-calc.hints.1'), t('ui.shipping-calc.hints.2')];
  const title = calculatorTitle ? calculatorTitle : t('ui.shipping-calc.title');

  const priceSuburbText =
    language.id === 'uz'
      ? `${meta.data.region.name} ${t('ui.shipping-calc.price-suburb')} — `
      : `${t('ui.shipping-calc.price-suburb')} ${meta.data.region.name} — `;

  const handleCalculate = useCallback(
    async (e: MouseEvent | KeyboardEvent, targetHint?: AddressHint) => {
      const hint = targetHint || selectedHint;

      if (targetHint) {
        setSelectedHint(targetHint);
      }

      if (!hint.data) {
        if (onSetDeliveryPrice) onSetDeliveryPrice(null);
        setPrice(null);
        setServerErrors([
          { name: 'address', message: t('ui.shipping-calc.errors.notSelectedAddress') },
        ]);
        return;
      }

      if (!hint.data.house) {
        if (onSetDeliveryPrice) onSetDeliveryPrice(null);
        setPrice(null);
        setServerErrors([
          { name: 'address', message: t('ui.shipping-calc.errors.notFullAddress') },
        ]);
        return;
      }

      setLoading(true);

      try {
        const params = {
          id: meta.data.region.id,
          latitude: +hint.data.latitude,
          longitude: +hint.data.longitude,
          region: meta.data.region.url.replace('/', ''),
          country,
        };
        const res = await ApiPecom.getDeliveryCostByRegion(params);

        if (onSetDeliveryPrice) onSetDeliveryPrice(res.data.cost);
        setPrice(res.data.cost);
      } catch (err) {
        const newErrors = err?.errors || [
          { name: 'address', message: t('ui.shipping-calc.errors.server') },
        ];

        if (onSetDeliveryPrice) onSetDeliveryPrice(null);
        setPrice(null);
        setServerErrors(newErrors);
      } finally {
        setLoading(false);
      }
    },
    [country, meta.data.region.id, meta.data.region.url, selectedHint, t, onSetDeliveryPrice],
  );

  const handleSubmit = useCallback(
    async (e) => {
      if (hints.length > 0) {
        // Если пользователь выбирал какую-то подсказку, то для расчета будет использоваться она, если адрес был введен вручную и после этого нажата
        // кнопка Рассчитать, то для расчета используем первую подсказку, как наиболее точно совпадающую
        const adress = selectedHint ? selectedHint : hints[0];
        await handleCalculate(e, adress);
      } else {
        if (onSetDeliveryPrice) onSetDeliveryPrice(null);
        setPrice(null);
        setServerErrors([
          { name: 'address', message: t('ui.shipping-calc.errors.notSelectedAddress') },
        ]);
      }
      setSelectedHint(null);
    },
    [hints, selectedHint, handleCalculate, t, onSetDeliveryPrice],
  );

  return (
    <div {...restProps} className={cn(styles.calculator, className)}>
      <div className={styles.label}>{title}</div>
      <Form
        className={styles.form}
        name='shipping-cost-calculator'
        serverErrors={serverErrors}
        onSubmit={handleSubmit}
      >
        <InputHelperAddress
          wide
          className={styles.input}
          placeholder={t('ui.shipping-calc.adress-placeholder')}
          name='address'
          onSelectHint={handleCalculate}
          onChangeHints={setHints}
        />
        <Button
          className={cn(styles.action, { [styles.modalView]: modalView })}
          wide
          theme='primary'
          waiting={loading}
          type='submit'
          data-testid='button-calculation'
        >
          {t('ui.shipping-calc.calculate')}
        </Button>
      </Form>

      {price && (!isRus || !modalView) && (
        <>
          <div className={styles.price}>
            {priceSuburbText}
            <Price price={price} />
          </div>

          {notes.map((note) => (
            <div className={styles.hint} key={note}>
              {note}
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default memo(ShippingCostCalculator);
